import React, { useState } from "react";
import { PlusCircle, Trash2 } from "lucide-react";

/**
 * A completely empty "initial" config
 * so each tenant (convention) can fill in their own data.
 */
const EMPTY_REGISTRATION_CONFIG = {
  // 1) Convention Pricing (e.g., "Mini," "Teen," "Teacher," etc.)
  pricingCategories: [],

  // 2) Age Divisions (each can have minAge, maxAge, and optional notes)
  ageDivisions: [],

  // 3) Registration Rules (bullet points that the admin can add/remove)
  registrationRules: [],

  // 4) Loyalty Program, Hotel Rewards, etc. (simple text fields for free-form usage)
  loyaltyProgram: "",
  hotelRewardPolicy: "",

  // 5) Payment Deadline (or multiple deadlines if you prefer)
  paymentDeadline: "",

  // 6) Parent Observer (free text or bullet approach)
  parentObserverPolicy: "",

  // 7) College Discount (maybe an array of tiers)
  collegeDiscountTiers: [],

  // 8) Multi-Event Discount (free text describing discount logic)
  multiEventDiscount: "",

  // 9) One Day Workshop policy
  oneDayWorkshopPolicy: "",

  // 10) Streetz Stars or any special kids program
  specialKidsProgram: "",

  // 11) Code of Conduct & Additional Policies
  codeOfConduct: "",
  videoAndPhotography: "",
  idBraceletsPolicy: "",
};

export default function RegistrationSettings() {
  // State for all registration config; starts empty
  const [registrationConfig, setRegistrationConfig] = useState(
    EMPTY_REGISTRATION_CONFIG
  );

  /* -------------------------------------------------------------------------- */
  /*                              1) PRICING CATEGORIES                          */
  /* -------------------------------------------------------------------------- */
  // Add a new pricing category
  const handleAddPricingCategory = () => {
    setRegistrationConfig((prev) => ({
      ...prev,
      pricingCategories: [
        ...prev.pricingCategories,
        {
          id: Date.now(),      // unique ID
          label: "",           // e.g. "Teen (13-15)"
          discountRate: 0,     // base price if early
          lateFee: 0,          // additional cost after cutoff
          notes: "",           // free text: "free," "partially discounted," etc.
        },
      ],
    }));
  };

  // Remove a pricing category
  const handleRemovePricingCategory = (catId) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      pricingCategories: prev.pricingCategories.filter((c) => c.id !== catId),
    }));
  };

  // Update a pricing category field
  const handlePricingCategoryChange = (catId, field, value) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      pricingCategories: prev.pricingCategories.map((c) =>
        c.id === catId ? { ...c, [field]: value } : c
      ),
    }));
  };

  /* -------------------------------------------------------------------------- */
  /*                             2) AGE DIVISIONS                               */
  /* -------------------------------------------------------------------------- */
  // Add a new age division
  const handleAddAgeDivision = () => {
    setRegistrationConfig((prev) => ({
      ...prev,
      ageDivisions: [
        ...prev.ageDivisions,
        {
          id: Date.now(),
          divisionName: "", // e.g. "Teen"
          minAge: 0,
          maxAge: 0,
          notes: "",        // any extra disclaimers
        },
      ],
    }));
  };

  // Remove an age division
  const handleRemoveAgeDivision = (divId) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      ageDivisions: prev.ageDivisions.filter((d) => d.id !== divId),
    }));
  };

  // Update an age division field
  const handleAgeDivisionChange = (divId, field, value) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      ageDivisions: prev.ageDivisions.map((d) =>
        d.id === divId ? { ...d, [field]: value } : d
      ),
    }));
  };

  /* -------------------------------------------------------------------------- */
  /*                           3) REGISTRATION RULES                            */
  /* -------------------------------------------------------------------------- */
  // Add a new rule (bullet point)
  const handleAddRule = () => {
    setRegistrationConfig((prev) => ({
      ...prev,
      registrationRules: [...prev.registrationRules, ""],
    }));
  };

  // Remove a rule
  const handleRemoveRule = (index) => {
    setRegistrationConfig((prev) => {
      const updated = [...prev.registrationRules];
      updated.splice(index, 1);
      return { ...prev, registrationRules: updated };
    });
  };

  // Update a rule's text
  const handleRuleChange = (index, newValue) => {
    setRegistrationConfig((prev) => {
      const updated = [...prev.registrationRules];
      updated[index] = newValue;
      return { ...prev, registrationRules: updated };
    });
  };

  /* -------------------------------------------------------------------------- */
  /*       4) LOYALTY PROGRAM & HOTEL REWARD (FREE TEXT FIELDS FOR FLEX)        */
  /* -------------------------------------------------------------------------- */
  const handleChangeLoyaltyProgram = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, loyaltyProgram: value }));
  };
  const handleChangeHotelReward = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, hotelRewardPolicy: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*          5) PAYMENT DEADLINE & PARENT OBSERVER POLICY (FREE TEXT)          */
  /* -------------------------------------------------------------------------- */
  const handleChangePaymentDeadline = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, paymentDeadline: value }));
  };
  const handleChangeParentObserver = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, parentObserverPolicy: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*                          7) COLLEGE DISCOUNT TIERS                          */
  /* -------------------------------------------------------------------------- */
  const handleAddCollegeTier = () => {
    setRegistrationConfig((prev) => ({
      ...prev,
      collegeDiscountTiers: [
        ...prev.collegeDiscountTiers,
        {
          id: Date.now(),
          range: "",          // e.g. "1-4 dancers"
          pricePerDancer: 0,  // base cost
          freeOrganizers: 0,  // how many free
        },
      ],
    }));
  };

  const handleRemoveCollegeTier = (tierId) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      collegeDiscountTiers: prev.collegeDiscountTiers.filter(
        (t) => t.id !== tierId
      ),
    }));
  };

  const handleCollegeTierChange = (tierId, field, value) => {
    setRegistrationConfig((prev) => ({
      ...prev,
      collegeDiscountTiers: prev.collegeDiscountTiers.map((t) =>
        t.id === tierId ? { ...t, [field]: value } : t
      ),
    }));
  };

  /* -------------------------------------------------------------------------- */
  /*                     8) MULTI-EVENT DISCOUNT (FREE TEXT)                    */
  /* -------------------------------------------------------------------------- */
  const handleChangeMultiEvent = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, multiEventDiscount: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*         9) ONE DAY WORKSHOP & 10) SPECIAL KIDS PROGRAM (FREE TEXT)         */
  /* -------------------------------------------------------------------------- */
  const handleChangeOneDayWorkshop = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, oneDayWorkshopPolicy: value }));
  };
  const handleChangeSpecialKidsProgram = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, specialKidsProgram: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*       11) CODE OF CONDUCT, VIDEO, ID BRACELETS (FREE TEXT FIELDS)          */
  /* -------------------------------------------------------------------------- */
  const handleChangeCodeOfConduct = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, codeOfConduct: value }));
  };
  const handleChangeVideoAndPhoto = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, videoAndPhotography: value }));
  };
  const handleChangeIdBraceletsPolicy = (value) => {
    setRegistrationConfig((prev) => ({ ...prev, idBraceletsPolicy: value }));
  };

  /* -------------------------------------------------------------------------- */
  /*                                 SAVE LOGIC                                  */
  /* -------------------------------------------------------------------------- */
  const handleSave = () => {
    // For demonstration, we just alert the JSON. 
    // In a real app, you'd POST to your backend or store in localStorage.
    alert("Registration Settings:\n" + JSON.stringify(registrationConfig, null, 2));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <h1 className="text-2xl font-bold">Registration Settings</h1>
      <p className="text-sm text-gray-600">
        Configure your registration logic here. No defaults are provided, so you 
        can fully customize for any convention.
      </p>

      {/* ------------------- 1) PRICING CATEGORIES ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Convention Pricing Categories</h2>
          <button
            onClick={handleAddPricingCategory}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <PlusCircle size={16} />
            Add Category
          </button>
        </div>
        {registrationConfig.pricingCategories.length === 0 && (
          <p className="text-sm text-gray-500">No pricing categories yet.</p>
        )}
        {registrationConfig.pricingCategories.map((cat) => (
          <div
            key={cat.id}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded border border-gray-100"
          >
            <div className="flex-1 space-y-1">
              <input
                type="text"
                placeholder="Label (e.g. 'Teen (13-15)')"
                value={cat.label}
                onChange={(e) =>
                  handlePricingCategoryChange(cat.id, "label", e.target.value)
                }
                className="border-b w-full focus:outline-none p-1"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <label className="flex items-center gap-1">
                  Discount Rate:
                  <input
                    type="number"
                    value={cat.discountRate}
                    onChange={(e) =>
                      handlePricingCategoryChange(
                        cat.id,
                        "discountRate",
                        +e.target.value || 0
                      )
                    }
                    className="border-b w-20 focus:outline-none p-1"
                  />
                </label>
                <label className="flex items-center gap-1">
                  Late Fee:
                  <input
                    type="number"
                    value={cat.lateFee}
                    onChange={(e) =>
                      handlePricingCategoryChange(
                        cat.id,
                        "lateFee",
                        +e.target.value || 0
                      )
                    }
                    className="border-b w-16 focus:outline-none p-1"
                  />
                </label>
              </div>
              <input
                type="text"
                placeholder="Notes (e.g. 'FREE', 'includes T-Shirt')"
                value={cat.notes}
                onChange={(e) =>
                  handlePricingCategoryChange(cat.id, "notes", e.target.value)
                }
                className="border-b w-full focus:outline-none p-1"
              />
            </div>
            <button
              onClick={() => handleRemovePricingCategory(cat.id)}
              className="text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={16} />
              Remove
            </button>
          </div>
        ))}
      </section>

      {/* ------------------- 2) AGE DIVISIONS ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Age Divisions</h2>
          <button
            onClick={handleAddAgeDivision}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <PlusCircle size={16} />
            Add Division
          </button>
        </div>
        {registrationConfig.ageDivisions.length === 0 && (
          <p className="text-sm text-gray-500">No age divisions added yet.</p>
        )}
        {registrationConfig.ageDivisions.map((div) => (
          <div
            key={div.id}
            className="p-3 bg-gray-50 rounded border border-gray-100 space-y-3"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <input
                type="text"
                placeholder="Division Name (e.g. 'Teen')"
                value={div.divisionName}
                onChange={(e) =>
                  handleAgeDivisionChange(div.id, "divisionName", e.target.value)
                }
                className="border-b p-1 w-full focus:outline-none"
              />
              <button
                onClick={() => handleRemoveAgeDivision(div.id)}
                className="text-red-500 hover:text-red-600 flex items-center gap-1"
              >
                <Trash2 size={16} />
                Remove
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <label className="flex items-center gap-1">
                Min Age:
                <input
                  type="number"
                  value={div.minAge}
                  onChange={(e) =>
                    handleAgeDivisionChange(div.id, "minAge", +e.target.value || 0)
                  }
                  className="border-b w-20 focus:outline-none p-1"
                />
              </label>
              <label className="flex items-center gap-1">
                Max Age:
                <input
                  type="number"
                  value={div.maxAge}
                  onChange={(e) =>
                    handleAgeDivisionChange(div.id, "maxAge", +e.target.value || 0)
                  }
                  className="border-b w-20 focus:outline-none p-1"
                />
              </label>
            </div>
            <textarea
              rows={2}
              placeholder="Additional notes or disclaimers for this division"
              value={div.notes}
              onChange={(e) => handleAgeDivisionChange(div.id, "notes", e.target.value)}
              className="border-b w-full focus:outline-none p-1"
            />
          </div>
        ))}
      </section>

      {/* ------------------- 3) REGISTRATION RULES (BULLET POINTS) ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Registration Rules</h2>
          <button
            onClick={handleAddRule}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <PlusCircle size={16} />
            Add Rule
          </button>
        </div>
        {registrationConfig.registrationRules.length === 0 && (
          <p className="text-sm text-gray-500">No rules have been added yet.</p>
        )}
        {registrationConfig.registrationRules.map((rule, idx) => (
          <div key={idx} className="flex items-start gap-2 mb-2">
            <span className="mt-1">•</span>
            <textarea
              rows={2}
              value={rule}
              onChange={(e) => handleRuleChange(idx, e.target.value)}
              className="border-b w-full p-1 focus:outline-none"
            />
            <button
              onClick={() => handleRemoveRule(idx)}
              className="text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </section>

      {/* ------------------- 4) LOYALTY & HOTEL REWARD (FREE TEXT) ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Loyalty Program & Hotel Reward</h2>
        <div className="space-y-2">
          <label className="block font-medium">Loyalty Program</label>
          <textarea
            rows={3}
            placeholder="Describe loyalty or bulk registration perks"
            value={registrationConfig.loyaltyProgram}
            onChange={(e) => handleChangeLoyaltyProgram(e.target.value)}
            className="border-b w-full focus:outline-none p-1"
          />
        </div>
        <div className="space-y-2">
          <label className="block font-medium">Hotel Reward Policy</label>
          <textarea
            rows={3}
            placeholder="Describe any hotel credits or reimbursements"
            value={registrationConfig.hotelRewardPolicy}
            onChange={(e) => handleChangeHotelReward(e.target.value)}
            className="border-b w-full focus:outline-none p-1"
          />
        </div>
      </section>

      {/* ------------------- 5) PAYMENT DEADLINE & PARENT OBSERVER ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Payment Deadline & Parent Observer</h2>
        <div className="space-y-2">
          <label className="block font-medium">Payment Deadline Policy</label>
          <textarea
            rows={3}
            placeholder="Explain when payment is due (days before event, etc.)"
            value={registrationConfig.paymentDeadline}
            onChange={(e) => handleChangePaymentDeadline(e.target.value)}
            className="border-b w-full focus:outline-none p-1"
          />
        </div>
        <div className="space-y-2">
          <label className="block font-medium">Parent Observer Policy</label>
          <textarea
            rows={3}
            placeholder="Describe rules for parents observing classes"
            value={registrationConfig.parentObserverPolicy}
            onChange={(e) => handleChangeParentObserver(e.target.value)}
            className="border-b w-full focus:outline-none p-1"
          />
        </div>
      </section>

      {/* ------------------- 7) COLLEGE DISCOUNT TIERS ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">College Discount Tiers</h2>
          <button
            onClick={handleAddCollegeTier}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
          >
            <PlusCircle size={16} />
            Add Tier
          </button>
        </div>
        {registrationConfig.collegeDiscountTiers.length === 0 && (
          <p className="text-sm text-gray-500">No college discount tiers yet.</p>
        )}
        {registrationConfig.collegeDiscountTiers.map((tier) => (
          <div
            key={tier.id}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded border border-gray-100"
          >
            <div className="flex-1 space-y-1">
              <input
                type="text"
                placeholder="Range (e.g. '1-4 dancers')"
                value={tier.range}
                onChange={(e) =>
                  handleCollegeTierChange(tier.id, "range", e.target.value)
                }
                className="border-b w-full focus:outline-none p-1"
              />
              <div className="flex gap-2">
                <label className="flex items-center gap-1">
                  Price/Dancer:
                  <input
                    type="number"
                    value={tier.pricePerDancer}
                    onChange={(e) =>
                      handleCollegeTierChange(tier.id, "pricePerDancer", +e.target.value || 0)
                    }
                    className="border-b w-20 focus:outline-none p-1"
                  />
                </label>
                <label className="flex items-center gap-1">
                  Free Organizers:
                  <input
                    type="number"
                    value={tier.freeOrganizers}
                    onChange={(e) =>
                      handleCollegeTierChange(tier.id, "freeOrganizers", +e.target.value || 0)
                    }
                    className="border-b w-16 focus:outline-none p-1"
                  />
                </label>
              </div>
            </div>
            <button
              onClick={() => handleRemoveCollegeTier(tier.id)}
              className="text-red-500 hover:text-red-600 flex items-center gap-1"
            >
              <Trash2 size={16} />
              Remove
            </button>
          </div>
        ))}
      </section>

      {/* ------------------- 8) MULTI-EVENT DISCOUNT ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Multi-Event Discount</h2>
        <textarea
          rows={3}
          placeholder="Describe multi-event discount logic"
          value={registrationConfig.multiEventDiscount}
          onChange={(e) => handleChangeMultiEvent(e.target.value)}
          className="border-b w-full focus:outline-none p-1"
        />
      </section>

      {/* ------------------- 9) ONE DAY WORKSHOP & 10) SPECIAL KIDS PROGRAM ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">One Day Workshop & Special Kids Program</h2>
        <div>
          <label className="block font-medium mb-1">One Day Workshop Policy</label>
          <textarea
            rows={2}
            placeholder="Describe one-day workshop rules/availability"
            value={registrationConfig.oneDayWorkshopPolicy}
            onChange={(e) => handleChangeOneDayWorkshop(e.target.value)}
            className="border-b w-full p-1"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Special Kids Program</label>
          <textarea
            rows={3}
            placeholder="Describe a special children's program, if any (like 'Streetz Stars')"
            value={registrationConfig.specialKidsProgram}
            onChange={(e) => handleChangeSpecialKidsProgram(e.target.value)}
            className="border-b w-full p-1"
          />
        </div>
      </section>

      {/* ------------------- 11) CODE OF CONDUCT & ADDITIONAL POLICIES ------------------- */}
      <section className="bg-white p-4 rounded border border-gray-200 space-y-4">
        <h2 className="text-lg font-semibold">Code of Conduct & Additional Policies</h2>
        <div>
          <label className="block font-medium mb-1">Code of Conduct</label>
          <textarea
            rows={3}
            placeholder="Rules for attendee behavior or possible removal"
            value={registrationConfig.codeOfConduct}
            onChange={(e) => handleChangeCodeOfConduct(e.target.value)}
            className="border-b w-full p-1"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Video & Photography</label>
          <textarea
            rows={2}
            placeholder="e.g. 'No videos during competition, only during classes.'"
            value={registrationConfig.videoAndPhotography}
            onChange={(e) => handleChangeVideoAndPhoto(e.target.value)}
            className="border-b w-full p-1"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">ID Bracelets Policy</label>
          <textarea
            rows={2}
            placeholder="e.g. 'Bracelets must remain on all weekend; lost bracelet => $50 replacement fee.'"
            value={registrationConfig.idBraceletsPolicy}
            onChange={(e) => handleChangeIdBraceletsPolicy(e.target.value)}
            className="border-b w-full p-1"
          />
        </div>
      </section>

      {/* ------------------- SAVE BUTTON ------------------- */}
      <button
        onClick={handleSave}
        className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
      >
        Save Registration Settings
      </button>
    </div>
  );
}
